import cloneDeep from 'lodash/cloneDeep';
import { SearchDateOption, SortOptions, SortOrder, StaticFilterKeywords, type SearchCategoryCount, type SortParameters } from '@mimir/common/dto/searchModels';
import type { SearchParams, SearchState as ItemSearchState, ViewSpecificDetails } from '@mimir/web/store/interfaces';
import { ViewType } from '@mimir/web/pages/search/constants';
import type { ClientItem, SearchResultHit } from '@mimir/common/dto/searchResultHit';
import Vue, { computed, reactive } from 'vue';
import { ArchiveRestoreStatus } from '@mimir/common/dto/itemPropTypes';
import type { FolderViewModel } from '@mimir/common/dto/folderModels';
import type { MetaDataSchema } from '@mimir/common/mdf/metadataSchema';
import { SYSTEM_FIELD_ID_QUERYPARAM, TECHNICAL_FIELD_ID_QUERYPARAM, type SearchResultViewModel } from '@mimir/common/search/interfaces';
import type { ItemMetaData } from '@mimir/common/dto/fieldValue';
import type { PersonResource } from '../pages/person/person-interfaces';
import type { ProgressInfo } from '@mimir/common/dto/progress';
import type { ItemCreationRequestDto } from '@mimir/common/dto/itemCreationRequestDto';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';
import isEmpty from 'lodash/isEmpty';
import eventBus, { EVENTS, showToast, TOAST } from '../eventBus';
import { ItemType } from '@mimir/common/dto/itemTypes';
import { getInjectionIndex } from '@mimir/common/websockets/wsItemInjection';
import { updateWsItemInjectionCount } from '../globals/wsItemInjection';
import type { VueDeleteValue, VueSetValue } from '@mimir/common/util/general';
import { handleMdfUpdatesToItem, type MdfUpdateItem } from '@mimir/common/mdf/update';
import { doesItemHavePlaceholderThumbnail, getThumbUrl } from '../util/thumbnail';
import { createPlaceholder, getItem, getItemFields, getLatestItemVersion } from '../util/api';
import { generateRouterLink } from '../util/route';
import { logErrorAndShowToast } from '../util/uiError';
import { ItemState } from '@mimir/common/dto/itemState';
import { extraInfoSticky, filterDelimiter, separateFiltersFromSearchString, serializeFilters } from '@mimir/common/filters/Filters';
import { addRouterLink } from '../pages/search/util';
import type { AxiosError } from 'axios';
import type {
  SearchState,
  SetDefaultDateFieldPayload,
  UpdateSearchHitById,
  ReplaceSearchHitById,
  InjectItemIntoSearchPayload,
  InsertUploadItemIntoSearchPayload,
  SingleItemSearchPayload
} from '@mimir/web/model/searchState';
import type { Logger } from '@mimir/common/types/logger';
import { createFinalSearchString } from '@mimir/common/filters/filter-util';
import type { Filters } from '@mimir/common/filters/Filters';
import type { SeparatedFilters } from '@mimir/common/filters/filter-util';


export const searchState : SearchState = createSearchState();

export function createSearchState(): SearchState {
  const internal : InternalState = reactive({
    itemsPerPage: 15,
    isLoadingSearch: false,
    typeCount: [],
    isRecycleBinSearch: false,
    searchForm: {
      searchString: '',
      rawSearchString: '',
      isFuzzy: false,
      atSameTime: false,
      showOnlyDeletedItems: false,
      sortBy: {
        property: SortOptions.ITEM_CREATED_DATE,
        order: SortOrder.DESCENDING
      },
      sortParamsList: [],
      dateTimeSearch: SearchDateOption.ANYTIME,
      defaultDateRangeField: StaticFilterKeywords.DateRange,
      defaultDateRangeFormId: undefined,
      useCustomSearchHook: false,
    },
    lastUsedSortBy: null,
    itemSearch: {
      searchString: '',
      startSearchFrom: 0,
      searchHasMoreHits: false,
      viewType: ViewType.LIST,
      total: 0,
      results: {
        count: 0,
        total: 0,
        from: 0,
        indexOfCurrentLoadedItemInHits: -1,
        typeCount: [],
        hits: [],
        dateHits: {},
        incompleteFolderCount: null,
      },
    },
    selectedSearchItems: [],
    lastSearchParams: {},
    customNoResultMessage: null,
    listOfFolderIdsInLastSearchResult: [],
    pathsForNewFolderHitsHasBeenLoaded: false,
    allTypesFilter: false,
  });

  const viewSpecificSettings: Record<ViewType, ViewSpecificDetails> = {
    [ViewType.LIST]: {
      itemsPerPage: 15
    },
    [ViewType.GRID]: {
      itemsPerPage: 30,
    },
    [ViewType.MAP]: {
      itemsPerPage: 100,
    },
    [ViewType.COMPACT]: {
      itemsPerPage: 100
    }
  };

  return {
    reactive: {
      customNoResultMessage: computed(() => internal.customNoResultMessage),
      indexOfCurrentLoadedItemInHits: computed(() => {
        return internal.itemSearch.results.indexOfCurrentLoadedItemInHits;
      }),
      isRecycleBinSearch: computed(() => internal.isRecycleBinSearch),
      searchString: computed(() => internal.searchForm.searchString),
      rawSearchString: computed(() => internal.searchForm.rawSearchString),
      itemsPerPage: computed(() => internal.itemsPerPage),
      searchForm: computed(() => internal.searchForm),
      itemSearch: computed(() => internal.itemSearch),
      isEmptySearch: computed(() => isEmpty(internal.itemSearch.results.hits)),
      typeCount: computed(() => internal.typeCount),
      isLoadingSearch: computed(() => internal.isLoadingSearch),
      currentViewType: computed(() => internal.itemSearch.viewType),
      selectedSearchItems: computed(() => internal.selectedSearchItems),
      sortBy: computed(() => internal.searchForm.sortBy),
      lastSearchParams: computed(() => internal.lastSearchParams),
      listOfFolderIdsInLastSearchResult: computed(() => internal.listOfFolderIdsInLastSearchResult),
      pathsForNewFolderHitsHasBeenLoaded: computed(() => internal.pathsForNewFolderHitsHasBeenLoaded),
      getlastUsedSortBy: computed(() => internal.lastUsedSortBy),
      isAllTypesFilter: computed(() => internal.allTypesFilter),
    },
    ops: {
      // From the store mutations

      isSelectedSearchItem(item: SearchResultHit): boolean {
        return internal.selectedSearchItems.some(selectedItem => selectedItem.id === item.id);
      },
      setCustomNoResultMessage(payload: string | null): void {
        internal.customNoResultMessage = payload;
      },
      setIndexOfCurrentLoadedItemInHits(payload: number): void {
        internal.itemSearch.results.indexOfCurrentLoadedItemInHits = payload;
      },
      setIsRecycleBinSearch(payload: boolean): void {
        internal.isRecycleBinSearch = payload;
      },
      resetSearch(): void {
        internal.itemSearch.startSearchFrom = 0;
        internal.itemSearch.total = 0;
        internal.itemSearch.results = {
          count: 0,
          total: 0,
          from: 0,
          indexOfCurrentLoadedItemInHits: -1,
          typeCount: [],
          hits: [],
          dateHits: {},
          incompleteFolderCount: null,
        };
        internal.typeCount = [];
      },
      clearSearchString(): void {
        internal.searchForm.rawSearchString = '';
        internal.searchForm.searchString = '';
      },
      setTypeCount(payload: SearchCategoryCount[]): void {
        internal.typeCount = payload;
      },
      setRawSearchString(value: string): void {
        internal.searchForm.rawSearchString = value;
      },
      setSearchString(value: string): void {
        internal.searchForm.searchString = value;
      },
      setItemSearch(value: ItemSearchState): void {
        internal.itemSearch = value;
      },
      setItemsPerPage(payload: number): void {
        internal.itemsPerPage = payload;
      },
      setSameTimeFilter(payload: boolean): void {
        internal.searchForm.atSameTime = payload;
      },
      setViewSpecificDetails(): void {
        internal.itemsPerPage = viewSpecificSettings[internal.itemSearch.viewType ?? ViewType.LIST].itemsPerPage;
      },
      setIsLoadingSearch(payload: boolean): void {
        internal.isLoadingSearch = payload;
      },
      setSortParamsList(payload: SortParameters[]): void {
        internal.searchForm.sortParamsList = payload;
      },
      setSearchFormSortParams(payload: { params: SortParameters, doNotChangeLevel?: boolean }): void {
        const { params, doNotChangeLevel } = payload;
        if (internal.itemSearch.viewType === ViewType.COMPACT) {
          const sortParamsList = internal.searchForm.sortParamsList;
          if (!sortParamsList) {
            // no previous list. make a new one.
            internal.searchForm.sortParamsList = [params];
          } else {
            // find if the old list already has this sort prop
            const previousIndex = sortParamsList.findIndex(sortParams => sortParams.property === params.property);
            if (previousIndex >= 0) {
              // if already there, remove it
              sortParamsList.splice(previousIndex, 1);
            }
            // At this point the param is removed from sortParamsList, if present in old list.
            // Now if order property is missing we should return. From header-context-menu user can clear column order.
            if (params.order === undefined || params.order === '') {
              return;
            }
            // should not change sort level when user only change sort order and the param has previousIndex > 0.
            if (doNotChangeLevel && previousIndex > 0) {
              sortParamsList.push(params);
            } else {
              // add it to the top as the most significant sort prop
              sortParamsList.unshift(params);
            }

            // if list grows more than max, truncate it to keep most recent sort props
            const maxSortCols = 2;
            if (sortParamsList.length > maxSortCols) {
              sortParamsList.length = maxSortCols;
            }
          }

          // do not modify the old sortBy prop
          return;
        }
        internal.searchForm.sortBy = params;
      },
      setSearchDate(payload: SearchDateOption): void {
        internal.searchForm.dateTimeSearch = payload;
      },
      setDefaultDateRangeField(payload: SetDefaultDateFieldPayload | undefined): void {
        if (payload?.fieldIdOrGuid) internal.searchForm.defaultDateRangeField = payload.fieldIdOrGuid;
        if (payload?.formId) internal.searchForm.defaultDateRangeFormId = payload.formId;
      },
      setLastUsedSortBy(payload: SortParameters | null): void {
        internal.lastUsedSortBy = payload;
      },
      lastSearchParams(payload: Record<string, string>): void {
        internal.lastSearchParams = payload;
      },
      setRestoreMediaCompletedInSearchResult(payload: { itemId: string, highRes: string | undefined, mediaOnlineUntil: string | undefined }): void {
        for (const item of internal.itemSearch.results.hits) {
          if (item && item.archiveInfo && ((item.id === payload.itemId)
          || (item.virtualClipInfo && item.virtualClipInfo.masterClipId === payload.itemId))) {
            // Not setting highRes as SearchResultHit does not need the highRes link
            Vue.set(item.archiveInfo, 'restoreStatus', ArchiveRestoreStatus.Completed);
            Vue.set(item.archiveInfo, 'mediaOnlineUntil', payload.mediaOnlineUntil);
          }
        }
      },
      setMediaRestoreStatusInSearchResult(payload: { itemId: string, restoreStatus: ArchiveRestoreStatus }): void {
        for (const item of internal.itemSearch.results.hits) {
          if (item && item.archiveInfo && ((item.id === payload.itemId)
            || (item.virtualClipInfo && item.virtualClipInfo.masterClipId === payload.itemId))) {
            Vue.set(item.archiveInfo, 'restoreStatus', payload.restoreStatus);
          }
        }
      },
      updateFolderInSearch(payload: { id: string, name?: string, owner?: string }): void {
        const { id, name, owner } = payload;
        if (internal.itemSearch.results) {
          const hit = internal.itemSearch.results.hits.find(item => item.id === id);
          if (hit) {
            if (name !== undefined) hit.name = name;
            if (owner !== undefined) hit.owner = owner;
          }
        }
      },
      injectFolderIntoSearchResult(payload: SearchResultHit): void {
        if (internal.itemSearch.results) {
          if (internal.itemSearch.results.hits.some(hit => hit.id === payload.id)) {
            showToast('Folder already present in the current folder', TOAST.INFO);
            return;
          }

          payload.itemType = ItemType.FOLDER;

          const hasFolders = internal.itemSearch.results.hits.some(hit => hit.itemType === ItemType.FOLDER);
          const hasNonFolders = internal.itemSearch.results.hits.some(hit => hit.itemType !== ItemType.FOLDER);

          // Can safely insert at the start.
          if (!hasFolders) {
            internal.itemSearch.results.hits.unshift(payload);
            return;
          }

          if (!hasNonFolders) {
            const unsorted = [...internal.itemSearch.results.hits];
            unsorted.push(payload);
            internal.itemSearch.results.hits = sortBy(unsorted, hit => hit.name);
          } else {
            const index = internal.itemSearch.results.hits.findIndex(hit => hit.itemType !== ItemType.FOLDER);
            let unsorted = internal.itemSearch.results.hits.splice(0, index);
            unsorted.push(payload);
            unsorted = sortBy(unsorted, hit => hit.name);
            internal.itemSearch.results.hits = [...unsorted, ...internal.itemSearch.results.hits];
          }
        }
      },
      injectItemsIntoSearchResult(payload: {items: SearchResultHit[], mdfs?: MetaDataSchema[], ignoreSorting?: boolean}): void {
        if (!internal.itemSearch.results) return;
        const { items, ignoreSorting, mdfs = [] } = payload;

        let firstNonFolderIndex = 0;
        for (let i = 0; i < internal.itemSearch.results.hits.length; i++) {
          if (internal.itemSearch.results.hits[i].itemType !== ItemType.FOLDER) {
            firstNonFolderIndex = i;
            break;
          }
        }

        // For version search, we need to inject versions in the order they are received. Versions are not fetched through search api.
        // We always ignore websocket item creation message for version view. So we do not have to worry about memory bloating.
        if (ignoreSorting) {
          internal.itemSearch.results.hits.splice(firstNonFolderIndex, 0, ...items);
          internal.itemSearch.results.total += items.length;
          internal.itemSearch.total += items.length;
          return;
        }

        const sortParamList = internal.searchForm.sortParamsList as SortParameters[];
        const sortFieldsInfo = extractSortFieldsInfo(sortParamList, mdfs);
        const isCompactView = internal.itemSearch.viewType === ViewType.COMPACT;
        const sorParams: SortParameters[] = isCompactView ? sortParamList : [internal.searchForm.sortBy as SortParameters];
        const isAllItemsLoaded = internal.itemSearch.results.hits.length === internal.itemSearch.total;

        const itemsToBeInjected: {item: SearchResultHit, index: number}[] = [];
        for (const item of items) {
          const index = getInjectionIndex(item, sorParams, internal.itemSearch.results.hits, isAllItemsLoaded, isCompactView, sortFieldsInfo);
          if (index !== null) {
            itemsToBeInjected.push({ item, index });
          }
        }
        if (itemsToBeInjected.length === 0) return;
        itemsToBeInjected.sort((a, b) => b.index - a.index);

        // Unnecessary memory bloating can happen when search view is left alone for a long time
        // while new items keep getting injected through WebSocket events. To prevent this, we
        // are evicting the bottom results from the currently loaded array before adding the new ones.
        const nonFolderItemsLength = internal.itemSearch.results.hits.length - firstNonFolderIndex;
        if (internal.itemSearch.results.hits.length > 100 && nonFolderItemsLength >= itemsToBeInjected.length) {
          internal.itemSearch.results.hits.length -= itemsToBeInjected.length;
          internal.itemSearch.results.total -= itemsToBeInjected.length;
        } else if (!isAllItemsLoaded) {
          // When we are injecting items into a search result and it is not fully loaded, we need to
          // adjust search parameters to consider the new items and fetching the next batch from appropriate index.
          // This is only relevant for continuation search.
          updateWsItemInjectionCount(itemsToBeInjected.length);
          internal.itemSearch.startSearchFrom += itemsToBeInjected.length;
        }

        for (const { item, index } of itemsToBeInjected) {
          internal.itemSearch.results.hits.splice(index, 0, item);
        }

        internal.itemSearch.results.total += itemsToBeInjected.length;
        internal.itemSearch.total += itemsToBeInjected.length;
      },
      removeFromSearchById(id: string): void {
        if (internal.itemSearch.results) {
          const index = internal.itemSearch.results.hits.findIndex(item => item.id === id);
          if (index >= 0) {
            internal.itemSearch.results.hits.splice(index, 1);
            internal.itemSearch.results.total--;
            internal.itemSearch.total--;
          }
        }
      },
      updateSearchHitById(payload: UpdateSearchHitById): void {
        const { id, update, retrievedFields } = payload;
        /*
          `payload.retrievedFields` contains a list of keys fetched from the server.
          Since `undefined` values are not transmitted over the API, we combine the
          fetched keys with those available in the `payload.update` object.

          This ensures that the client state is updated with the most recent data
          while correctly handling stale state updates.
        */
        const uniqueKeys = new Set([...Object.keys(update), ...(retrievedFields ?? [])]);
        const keysToIterateOver = Array.from(uniqueKeys) as (keyof SearchResultHit)[];

        const searchHit = internal.itemSearch.results.hits.find(hit => hit.id === id);
        if (!searchHit) {
          return;
        }
        const doesUpdateHaveMedia = keysToIterateOver.find(el => el === 'media');
        const vueSet = (v: VueSetValue) => Vue.set(v.object, v.key, v.value);
        const vueDeleter = (v: VueDeleteValue) => Vue.delete(v.object, v.key);
        for (const prop of keysToIterateOver) {
          if (prop === 'metadata' && update.metadata !== undefined) {
            let metadataVersion = update.metadataVersion;
            if (metadataVersion == undefined) {
              console.warn('updateSearchHitById: metadata update without metadataVersion');
              metadataVersion = 0;
            }
            handleMdfUpdatesToItem(searchHit, update.metadata, metadataVersion, vueSet, vueDeleter);
            continue;
          }
          // in shouldPollForUpdates we use 'media' property as the source of truth if kelda live item's
          // thumbnail is available. but there is a slight window where thumbnail is available but item
          // doesn't yet have the 'media' property. if a user reloads the folder during this time period
          // the thumbnail flashes black. this check addresses this problem by checking if the item already
          // has a valid thumbnail url
          if (
            prop === 'thumbnail' &&
            doesUpdateHaveMedia &&
            !doesItemHavePlaceholderThumbnail(searchHit as ClientItem)
          ) {
            continue;
          }

          Vue.set(searchHit, prop, update[prop]);
        }
      },
      replaceSearchHitById(payload: ReplaceSearchHitById): void {
        const { newItem, itemIdToReplace } = payload;
        const hits = internal.itemSearch.results.hits;
        const index = hits.findIndex(hit => hit.id === itemIdToReplace);
        if (index < 0) {
          return;
        }
        const wasSelected = hits[index].selected;
        Vue.set(hits, index, newItem);
        if (wasSelected)  {
          internal.selectedSearchItems.push(newItem);
          Vue.set(newItem, 'selected', true);
        }
      },
      removeSelectedItemsFromSearchResult(payload: { onlyFolders: boolean, excludeId: string }): void {
        const { onlyFolders, excludeId } = payload;
        const hits = internal.itemSearch.results.hits;
        for (const selectedItem of internal.selectedSearchItems.filter(hit => hit.id !== excludeId)) {
          const index = hits.findIndex(hit => hit.id === selectedItem.id);
          if (index >= 0) {
            const hit = hits[index];
            if (onlyFolders && hit.itemType !== ItemType.FOLDER) {
              continue;
            }
            hits.splice(index, 1);
          }
        }
      },
      addToSelectedSearchItems(item: SearchResultHit | null | undefined): void {
        if (!item) return;

        const idx = internal.selectedSearchItems.findIndex(selectedItem => selectedItem.id === item.id);
        if (idx === -1) {
          internal.selectedSearchItems.push(item);
          Vue.set(item, 'selected', true);
        }
      },
      removeIdsFromSearchResult(payload: { onlyFolders?: boolean, idsToRemove: string[] }): void {
        const { onlyFolders, idsToRemove } = payload;
        const hits = internal.itemSearch.results.hits;
        for (const id of idsToRemove) {
          const index = hits.findIndex(hit => hit.id === id);
          if (index >= 0) {
            const hit = hits[index];
            if (onlyFolders && hit.itemType !== ItemType.FOLDER) {
              continue;
            }
            hits.splice(index, 1);
          }
        }
      },
      removeFromSelectedSearchItems(item: SearchResultHit | null | undefined): void {
        if (!item) return;

        const idx = internal.selectedSearchItems.findIndex(selectedItem => selectedItem.id === item.id);
        if (idx === -1) return;

        internal.selectedSearchItems.splice(idx, 1);
        Vue.set(item, 'selected', false);
      },
      clearSelectedSearchItems(): void {
        if (!internal.selectedSearchItems.length) return;
        internal.selectedSearchItems.map((item: SearchResultHit) => {
          Vue.set(item, 'selected', false);
        });
        internal.selectedSearchItems = [];
      },
      resetSearchResultArray(): void {
        internal.itemSearch.results.count = 0;
        internal.itemSearch.results.hits = [];
      },
      updateMetadataOfSearchHitItem(payload: {id: string, metadata: ItemMetaData, metadataVersion: number }): void {
        const { id, metadata, metadataVersion } = payload;
        const vueSet = (v: VueSetValue) => Vue.set(v.object, v.key, v.value);
        const vueDeleter = (v: VueDeleteValue) => Vue.delete(v.object, v.key);
        const itemFromSearch: MdfUpdateItem | undefined = internal.itemSearch.results.hits.find(hit => hit.id === id);
        if (itemFromSearch) {
          handleMdfUpdatesToItem(itemFromSearch, metadata, metadataVersion, vueSet, vueDeleter);
        }
      },
      /**
       * Append search hits to itemSearch, and update isRecycleBinSearch and
       * typeCount.
       */
      appendSearchHits(payload: SearchResultViewModel): void {
        const searchState = internal.itemSearch;
        searchState.total = payload.total;
        if (payload.from + payload.hits.length < payload.total) {
          searchState.searchHasMoreHits = true;
          searchState.startSearchFrom = payload.from + payload.hits.length;
        } else {
          searchState.searchHasMoreHits = false;
        }

        if (searchState.results && searchState.results.hits) {
          searchState.results.hits = searchState.results.hits.concat(payload.hits);
        } else {
          searchState.results = payload;
        }

        payload.hits.forEach(hit => {
          if (hit.itemType === ItemType.FOLDER) internal.listOfFolderIdsInLastSearchResult.push(hit.id);
        });

        if (searchState.results.dateHits && !isEqual(searchState.results.dateHits, payload.dateHits)) {
          searchState.results.dateHits = payload.dateHits;
        }

        internal.isRecycleBinSearch = payload.isRecycleBin;
        if (!payload.isRecycleBin) {
          internal.typeCount = payload.typeCount;
        }
      },
      setPathsForNewFolderHitsHasBeenLoaded(val: boolean): void {
        internal.pathsForNewFolderHitsHasBeenLoaded = val;
      },
      setListOfFolderIdsInLastSearchResult(payload: string[]): void {
        internal.listOfFolderIdsInLastSearchResult = payload;
      },
      setAllTypesFilter(payload: boolean): void {
        internal.allTypesFilter = payload;
      },


      // From the store actions

      /**
       * There was an action in the old vuex store module called
       * setDefaultDateRangeField, but this was mutating a bunch of other stuff
       * in the global store so it has been moved to 'setDefaultDateRangeField.ts'
       */

      /**
       * There was an action called resetSearch() here. This has been moved to
       * its own file 'resetSearchState.ts'
       */

      async singleItemSearch(payload: SingleItemSearchPayload): Promise<void> {
        const {
          itemId,
          currentFolder,
          loadItem,
        } = payload;

        // First determine if we already did this search, to avoid unnecessary searches when user
        // swaps between view types for example.
        if (internal.itemSearch.results.hits.length === 1 && internal.itemSearch.results.hits[0].id === itemId) {
          return;
        }

        this.resetSearch();
        this.setIsLoadingSearch(true);
        this.clearSelectedSearchItems();
        try {
          const item = await getItem(itemId);
          try {
            const latestVersion = await getLatestItemVersion(itemId);
            if (latestVersion.id !== itemId) {
              item.latestVersionId = latestVersion.id;
            }
          } catch(err) {
            console.warn(`Could not get latest version of item ${itemId}`, err);
          }
          item.routerLink = generateRouterLink(item.id, internal.lastSearchParams, item.itemType);
          this.injectItemIntoSearchResult({ item, shouldUpdateExisting: false, currentFolder });
          loadItem(item);
          this.addToSelectedSearchItems(item);
        } catch (err: any) {
          if (err?.response?.data?.error?.statusCode === 404) {
            this.setCustomNoResultMessage(`Item with id '${itemId}' could not be found.`);
          } else {
            logErrorAndShowToast(err?.response?.data?.error?.message || 'Could not load item search result', err, 'ex in singleItemSearch');
          }
        } finally {
          this.setIsLoadingSearch(false);
        }
      },
      // TODO: All places that call this must call the following code:
      //if (isInElectron(payload.applicationContext)) {
      //  return loadFolderPathForItem(payload.isProxyMode, payload.applicationContext, dispatch, payload.item);
      //}
      injectItemIntoSearchResult(payload: InjectItemIntoSearchPayload): void {
        const { item, shouldUpdateExisting, currentFolder } = payload;
        if (internal.itemSearch.results) {

          if (!doesItemBelongInCurrentFolder(item, currentFolder)) return;

          let firstNonFolderIndex = -1;
          for (let i = 0; i < internal.itemSearch.results.hits.length; i++) {
            const hit = internal.itemSearch.results.hits[i];
            if (hit.itemType !== ItemType.FOLDER && firstNonFolderIndex === -1) {
              firstNonFolderIndex = i;
            }
            // If the item already exists - don't inject.
            if (hit.id === item.id) {
              if (shouldUpdateExisting) Vue.set(internal.itemSearch.results.hits, i, { ...hit, ...item });
              return;
            }
          }

          if (firstNonFolderIndex < 0) {
            internal.itemSearch.results.hits.push(item);
          } else {
            internal.itemSearch.results.hits.splice(firstNonFolderIndex, 0, item);
          }
          internal.itemSearch.results.total++;
          internal.itemSearch.total++;
        }
      },
      insertUploadItemIntoSearch(payload: InsertUploadItemIntoSearchPayload): void {
        const {
          uploadItem,
          addRouterLink,
          currentFolder,
          loadFolderPathForItem,
        } = payload;

        uploadItem.addCreatedItemCallback(() => {
          const hits = internal.itemSearch.results?.hits;
          if (hits) {
            if (!doesItemBelongInCurrentFolder(uploadItem, currentFolder)) return;

            let index = -1;
            let existingItem: SearchResultHit | undefined = undefined;
            // First check if we've optimistically inserted a placeholder with a fake id.
            const fakeItemIndex = hits.findIndex(hit => hit.id === uploadItem.localId);
            if (fakeItemIndex >= 0) {
              index = fakeItemIndex;
            } else {
              // Otherwise check if we have one with a proper id we need to replace.
              const realItemIndex = hits.findIndex(hit => hit.id === uploadItem.id);
              if (realItemIndex >= 0) {
                existingItem = hits[realItemIndex];
                index = realItemIndex;
              }
            }

            const searchHit: ClientItem = {
              vttUrl: '',
              itemType: uploadItem.derivedItemType,
              name: uploadItem.title,
              metadata: uploadItem.metadata as ItemMetaData,
              id: uploadItem.id,
              thumbnail: existingItem?.thumbnail ?? '',
              operationsLink: '',
              originalFileName: uploadItem.file.name,
              proxy: null,
              itemState: uploadItem.itemState !== null ? uploadItem.itemState : ItemState.NEW,
              createdOn: new Date().toISOString(),
              mediaDuration: 0,
              routerLink: addRouterLink ? generateRouterLink(uploadItem.id, internal.lastSearchParams, uploadItem.derivedItemType) : undefined,
              folderParents: uploadItem.folderParents ?? [],
              createdBy: uploadItem.createdBy,
              languageCode: uploadItem.itemAnalyticsProperties?.languageCode,
              visibleTo: uploadItem.visibleTo,
            };


            if (loadFolderPathForItem) {
              loadFolderPathForItem(searchHit);
            }

            // Object was to be replaced. Update the selection references
            if (index >= 0) {
              const updatePayload: UpdateSearchHitById = {
                id: uploadItem.id,
                update: searchHit,
              };
              this.updateSearchHitById({
                id: uploadItem.id,
                update: updatePayload,
              });
            } else {
              return this.injectItemIntoSearchResult({
                item: searchHit,
                shouldUpdateExisting: false,
                currentFolder: currentFolder,
              });
            }
          }
        });

      },
      async updateSearchHitThumbnailById(payload: string): Promise<void> {
        const searchHit = internal.itemSearch.results.hits.find(hit => hit.id === payload);
        if (!searchHit) return;
        const obj = await getItemFields(payload, ['thumbnail']);
        searchHit.thumbnail = obj.thumbnail;
      },
      async searchResultsReceived(payload: { results: SearchResultViewModel }): Promise<void> {
        this.appendSearchHits(payload.results);
      },
      async doStickySearch(payload: string, clearFilters: (clearSticky: boolean) => void): Promise<void> {
        const parseResult = separateFiltersFromSearchString(payload);
        parseResult.parsedFilters.forEach(p => p.value = `${p.value}${filterDelimiter}${extraInfoSticky}`);
        const newSearchString = `${parseResult.remainingSearchString} ${parseResult.parsedFilters.map(p => `${p.token}:${p.value} `).join('')}`.trim();
        this.setSearchString(newSearchString);

        clearFilters(true);

        eventBus.emit(EVENTS.DO_SEARCH);
      },
      updatePersonWithRouterLink(person: PersonResource): void {
        addRouterLink([person], {});
      },
      async createPlaceHolderAndSearchHit(payload: {
        item: Partial<ItemCreationRequestDto>,
        inject: boolean,
        currentFolder: FolderViewModel | null,
      }): Promise<SearchResultHit> {
        const { item, inject, currentFolder } = payload;
        return new Promise((resolve, reject) => {
          createPlaceholder(item).then(response => {
            showToast('New item created', TOAST.SUCCESS);
            const searchHit: ClientItem = {
              name: '',
              itemType: ItemType.FILE,
              mediaDuration: 0,
              thumbnail: getThumbUrl('', response.itemType),
              proxy: null,
              ...response,
              itemState: ItemState.NEW,
              routerLink: generateRouterLink(response.id, internal.lastSearchParams, ItemType.FILE),
            };

            if (inject) {
              this.injectItemIntoSearchResult({ item: searchHit, shouldUpdateExisting: false, currentFolder });
            }

            resolve(searchHit);
          }).catch(err => {
            const errorMessageFromServer = (err as Partial<AxiosError>)?.response?.data?.error?.message;
            showToast(errorMessageFromServer ?? 'Something went wrong creating new item', TOAST.ERROR);
            reject(err);
          });
        });
      },
      injectProgressInfo({itemId, progressInfo}: { itemId: string, progressInfo?: ProgressInfo}): void {
        for (const hit of internal.itemSearch.results.hits) {
          if (hit.id === itemId) {
            Vue.set(hit, 'progressInfo', progressInfo ?? { status: 'Preparing ..' });
            break;
          }
        }
      },
      updateFromQueryParams(
        logger: Logger,
        filters: Filters,
        mdfs: MetaDataSchema[],
        separatedFilters: SeparatedFilters,
        queryParams: any
      ): void {
        const { searchString, viewType, isFuzzy, atSameTime, sortParamsList, defaultDateRangeField, defaultDateRangeFormId, useCustomSearchHook, allTypes } = queryParams;

        this.setDefaultDateRangeField({ fieldIdOrGuid: defaultDateRangeField, formId: defaultDateRangeFormId });
        if (allTypes === 'true' || allTypes === 'false') {
          this.setAllTypesFilter(allTypes === 'true');
        }

        // Any unrecognized filter at this point will be part of the remaining search string.
        const finalSearchString = createFinalSearchString(separatedFilters);

        const serializedFilterString = serializeFilters(filters, mdfs);
        internal.searchForm.rawSearchString = searchString?.length > 0 ? searchString : serializedFilterString;
        internal.itemSearch.searchString = internal.searchForm.searchString = finalSearchString;

        const folderSearchText = filters.defaultFilters['filter:folder'].getValue();
        const isFolderOnly = filters.defaultFilters['filter:itemType'].selectedCategories.length === 1 && (filters.defaultFilters['filter:itemType'].getValue() === ItemType.FOLDER);

        // If we have a folder search text we should clear the folder text filter
        // and set the search string to the folder search text.
        if (isFolderOnly && folderSearchText) {
          internal.itemSearch.searchString = internal.searchForm.searchString = folderSearchText;
          filters.defaultFilters['filter:folder'].clearValue();
        }

        internal.itemSearch.viewType = (Object.values(ViewType).includes(viewType)) ? viewType as ViewType : ViewType.LIST;

        if ('isFuzzy' in queryParams) {
          internal.searchForm.isFuzzy = typeof isFuzzy === 'boolean' ? isFuzzy : isFuzzy === 'true';
        }

        if ('atSameTime' in queryParams) {
          internal.searchForm.atSameTime = typeof atSameTime === 'boolean' ? atSameTime : atSameTime === 'true';
        }

        if ('useCustomSearchHook' in queryParams) {
          internal.searchForm.useCustomSearchHook = typeof useCustomSearchHook === 'boolean' ? useCustomSearchHook : useCustomSearchHook === 'true';
        }
        if (sortParamsList) {
          try {
            const sortParamsListParsed = JSON.parse(sortParamsList);
            this.setSortParamsList(sortParamsListParsed);
          } catch (err) {
            logger.error(`Could not parse sortParamsList: ${sortParamsList}`);
          }
        }

        // if dateRange filter is set and we have dateTimeSearch is set as Anytime then it can only
        // happen during page reload and hence setting custom option here.
        if (filters.dateFilter.hasValue && internal.searchForm.dateTimeSearch === SearchDateOption.ANYTIME) {
          this.setSearchDate(SearchDateOption.CUSTOM);
        }
      },
      toggleFuzzySearch(): void {
        internal.searchForm.isFuzzy = !internal.searchForm.isFuzzy;
      },
      toggleCustomSearchHook(): void {
        internal.searchForm.useCustomSearchHook = !internal.searchForm.useCustomSearchHook;
      },
      disableCustomSearchHook(): void {
        internal.searchForm.useCustomSearchHook = false;
      },
      cloneSearchParams(): SearchParams {
        return cloneDeep(internal.searchForm);
      }
    }
  };
}

interface InternalState {
  itemsPerPage: number,
  isLoadingSearch: boolean,
  searchForm: SearchParams,
  itemSearch: ItemSearchState,
  typeCount: SearchCategoryCount[],
  selectedSearchItems: SearchResultHit[],
  isRecycleBinSearch: boolean;

  // Used to keep track of the last search, mainly to generate router links on items so the user
  // can always return to the last search performed.
  lastSearchParams: Record<string, string>;
  customNoResultMessage: string | null;
  listOfFolderIdsInLastSearchResult: string[];
  pathsForNewFolderHitsHasBeenLoaded: boolean;
  lastUsedSortBy: SortParameters | null;
  allTypesFilter: boolean;
}

const extractSortFieldsInfo = (params: SortParameters[], mdfs: MetaDataSchema[]) => {
  const sortFieldsInfo: Record<string, { id: string }> = {};
  for (const param of params) {
    // If mdfSource is a regular schema then we need to extract the id. So that we can extract
    // the field value from websocket item metadata.
    if (param?.fieldId && param.mdfSource && param.mdfSource !== SYSTEM_FIELD_ID_QUERYPARAM && param.mdfSource !== TECHNICAL_FIELD_ID_QUERYPARAM) {
      const mdfToUse = mdfs.find(mdf => mdf.label === param.mdfSource);
      if (!mdfToUse) continue;
      if (mdfToUse.fields) {
        const field = mdfToUse.fields.find(f => f.fieldId === param.fieldId);
        if (field) {
          sortFieldsInfo[param.fieldId] = {
            id: field.id
          };
        }
      }
    }
  }
  return sortFieldsInfo;
};

/**
 * Checks if the given item is a child of the currently loaded folder.
 * Returns true if:
 * - There is no current folder loaded
 * - Item doesn't have any folder parents
 * - Item is a child of the currently loaded folder
 *
 * TODO: This one does not belong here. It has nothing to do with the SearchState
 */
export const doesItemBelongInCurrentFolder = (item: { folderParents: string[] | null | undefined }, currentFolder: FolderViewModel | null) => {
  const uploadItemParents = item.folderParents ?? [];
  if (currentFolder && uploadItemParents.length > 0 && !(uploadItemParents.includes(currentFolder.id))) {
    return false;
  }
  return true;
};
